import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FormProvider, useWatch } from 'react-hook-form-v7';
import type { FieldPath } from 'react-hook-form-v7';
import { Card, cn } from '@parrot-software/design-system';
import { logger } from '@parrot-software/observability';

import { OnboardingWizardProvider, useOnboardingWizardContext, WizardActions } from '@onboarding/presentation/context';
import {
  useCompleteWizard,
  useExistingAreaNames,
  useOnboardingWizard,
  useOnboardingWizardForm,
} from '@onboarding/presentation/hooks';
import {
  WizardContainer,
  WizardHeader,
  WizardNavigation,
  WizardProgress,
  WizardTopBar,
} from '@onboarding/presentation/components';
import { StepTransition } from '@onboarding/presentation/components/shared';
import {
  RestaurantTypeStep,
  TableServiceStep,
  TaxStep,
  CashRegisterStep,
  TipStep,
  ReceiptStep,
  CompletionStep,
  NextStepsStep,
} from '@onboarding/presentation/components/steps';
import { DEFAULT_TIP_PERCENT_OPTIONS, WIZARD_STEPS } from '@onboarding/domain/value-objects';
import type { WizardFormValues } from '@onboarding/presentation/types';

const onbLog = logger.forDomain('ONB');

/** Fields that must validate before advancing past each step. */
const STEP_FIELDS: Record<string, FieldPath<WizardFormValues>[]> = {
  [WIZARD_STEPS.RESTAURANT_TYPE]: ['restaurantType'],
  [WIZARD_STEPS.TABLE_SERVICE]: ['tableService'],
  [WIZARD_STEPS.CASH_REGISTER]: ['cashRegister'],
  [WIZARD_STEPS.TIP]: ['tip'],
  [WIZARD_STEPS.TAX]: ['tax'],
  [WIZARD_STEPS.RECEIPT]: ['receipt'],
};

/**
 * Inner wizard content — uses the wizard context and the form context.
 * Renders the current step inside a Card with progress and navigation.
 */
const WizardContent = () => {
  const history = useHistory();
  const { t } = useTranslation('onboarding');
  const {
    state,
    currentStepId,
    totalSteps,
    isSaving,
    savingError,
    isFirstStep,
    isLastStep,
    isCompletionStep,
    handleNext,
    handleBack,
    handleGoToStep,
    applyTableServiceConfig,
    isApplyingTableService,
    applyTableServiceError,
  } = useOnboardingWizard();
  const { dispatch } = useOnboardingWizardContext();
  const {
    mutateAsync: completeWizardAsync,
    isLoading: isCompletingWizard,
    error: completeWizardError,
  } = useCompleteWizard();
  const existingAreaNames = useExistingAreaNames();

  const formMethods = useOnboardingWizardForm();
  const { trigger, control, getValues, setError, clearErrors } = formMethods;

  const values = useWatch({ control }) as WizardFormValues;
  const [showNextSteps, setShowNextSteps] = useState(false);
  const prevStepRef = useRef(state.currentStep);
  const direction = state.currentStep >= prevStepRef.current ? 'forward' : 'backward';
  const wizardStartedAtRef = useRef<number>(Date.now());
  const tablesSkippedRef = useRef<boolean>(false);

  if (state.currentStep !== prevStepRef.current) {
    prevStepRef.current = state.currentStep;
  }

  useEffect(() => {
    onbLog.info('wizard_started');
  }, []);

  const handleAdvance = async () => {
    const fields = currentStepId ? STEP_FIELDS[currentStepId] : undefined;

    // Clear stale area errors before triggering validation when skip is active,
    // so the resolver evaluates the schema without leftover onBlur errors.
    if (currentStepId === WIZARD_STEPS.TABLE_SERVICE && getValues('tableService.skipAreaConfig')) {
      clearErrors('tableService.areas');
    }

    const isValid = fields ? await trigger(fields) : true;
    if (!isValid) return;

    if (currentStepId === WIZARD_STEPS.TABLE_SERVICE) {
      const tableService = getValues('tableService');

      // Cross-check against existing area names registered in backend (not covered by yup schema).
      if (!tableService.skipAreaConfig) {
        let hasExistingNameCollision = false;
        tableService.areas.forEach((area, index) => {
          const normalized = area.name.trim().toLowerCase();
          if (normalized && existingAreaNames.has(normalized)) {
            setError(`tableService.areas.${index}.name`, {
              type: 'manual',
              message: t('steps.tableService.validation.areaNameExists'),
            });
            hasExistingNameCollision = true;
          }
        });
        if (hasExistingNameCollision) return;
      }

      if (tableService.skipAreaConfig) {
        tablesSkippedRef.current = true;
        onbLog.info('wizard_step_skipped_tables', {
          'onb.has_table_service': tableService.hasTableService === 'true',
        });
      }

      // Table service config is submitted at wizard completion alongside wizardCompleted
      // (see handleFinishWizard). Form state holds the values until then.
      handleNext();
      return;
    }

    if (currentStepId === WIZARD_STEPS.CASH_REGISTER) {
      handleNext({ blindClosing: getValues('cashRegister') === 'blind' });
      return;
    }

    if (currentStepId === WIZARD_STEPS.TIP) {
      handleNext({ enabled: getValues('tip') === 'true' });
      return;
    }

    if (currentStepId === WIZARD_STEPS.TAX) {
      handleNext({ rate: getValues('tax') });
      return;
    }

    if (currentStepId === WIZARD_STEPS.RECEIPT) {
      const receipt = getValues('receipt');
      const isSingleBrand = (receipt.brands ?? []).length === 1;

      handleNext({
        businessName: receipt.businessName,
        legalName: receipt.legalName,
        rfc: receipt.rfc,
        fiscalAddress: receipt.fiscalAddress,
        brandChoices: (receipt.brands ?? []).map((b) => ({
          brandUuid: b.brandUuid,
          brandName: b.brandName,
          useRegisteredData: isSingleBrand ? false : b.useRegisteredData === 'true',
          customLegalName: b.legalName,
          customRfc: b.rfc,
          customFiscalAddress: b.fiscalAddress,
        })),
      });
      return;
    }

    if (currentStepId === WIZARD_STEPS.RESTAURANT_TYPE) {
      handleNext({ restaurantType: getValues('restaurantType') });
      return;
    }

    handleNext();
  };

  const isStepComplete = (() => {
    switch (currentStepId) {
      case WIZARD_STEPS.RESTAURANT_TYPE:
        return !!values.restaurantType;
      case WIZARD_STEPS.TABLE_SERVICE: {
        if (!values.tableService.hasTableService) return false;
        if (values.tableService.hasTableService === 'true' && !values.tableService.skipAreaConfig) {
          const { areas } = values.tableService;
          return areas.length > 0 && areas.every((area) => !!area.name.trim() && !!area.tableCount.trim());
        }
        return true;
      }
      case WIZARD_STEPS.CASH_REGISTER:
        return !!values.cashRegister;
      case WIZARD_STEPS.TIP:
        return !!values.tip;
      case WIZARD_STEPS.TAX:
        return !!values.tax;
      case WIZARD_STEPS.RECEIPT:
        return true;
      default:
        return true;
    }
  })();

  const isFullScreenStep = isCompletionStep || showNextSteps;

  const renderStep = () => {
    if (showNextSteps) {
      return <NextStepsStep onStart={() => history.push('/')} />;
    }

    if (isCompletionStep) {
      const handleFinishWizard = async () => {
        try {
          const tableService = getValues('tableService');
          await applyTableServiceConfig({
            hasTableService: tableService.hasTableService === 'true',
            areas: tableService.skipAreaConfig
              ? []
              : tableService.areas.map((area) => ({
                  name: area.name,
                  tableCount: Number(area.tableCount),
                  prefix: area.prefix,
                })),
          });

          await completeWizardAsync();
          dispatch({ type: WizardActions.WIZARD_COMPLETED });

          onbLog.info('wizard_completed', {
            'onb.total_duration_ms': Date.now() - wizardStartedAtRef.current,
            'onb.restaurant_type': values.restaurantType,
            'onb.has_tables': values.tableService.hasTableService === 'true',
            'onb.tables_skipped': tablesSkippedRef.current,
            'onb.tip_enabled': values.tip === 'true',
            'onb.tax_rate': values.tax,
            'onb.cash_register_type': values.cashRegister,
          });

          setShowNextSteps(true);
        } catch {
          // Error surfaces via applyTableServiceError / completeWizardError below.
        }
      };

      const finishErrorMessage =
        completeWizardError || applyTableServiceError ? t('steps.completion.errors.completeWizardFailed') : undefined;

      return (
        <CompletionStep
          errorMessage={finishErrorMessage}
          isFinishing={isCompletingWizard || isApplyingTableService}
          onEdit={handleGoToStep}
          onFinish={handleFinishWizard}
          summary={{
            restaurantType: values.restaurantType,
            tipEnabled: values.tip === 'true',
            tipPercentages: [...DEFAULT_TIP_PERCENT_OPTIONS],
            cashRegisterType: values.cashRegister,
            taxRate: values.tax,
            receiptConfigured: !!values.receipt.businessName.trim(),
          }}
        />
      );
    }

    switch (currentStepId) {
      case WIZARD_STEPS.RESTAURANT_TYPE:
        return <RestaurantTypeStep />;
      case WIZARD_STEPS.TABLE_SERVICE:
        return <TableServiceStep />;
      case WIZARD_STEPS.TIP:
        return <TipStep />;
      case WIZARD_STEPS.CASH_REGISTER:
        return <CashRegisterStep />;
      case WIZARD_STEPS.TAX:
        return <TaxStep />;
      case WIZARD_STEPS.RECEIPT:
        return <ReceiptStep />;
      default:
        return <p className="text-muted-foreground">{t('wizard.stepPlaceholder')}</p>;
    }
  };

  return (
    <FormProvider {...formMethods}>
      <WizardContainer topBar={<WizardTopBar />}>
        <div className="flex flex-1 justify-center px-4 py-6 md:items-stretch md:px-8 md:py-8 lg:items-center">
          <Card
            className={cn(
              'flex w-full max-w-[704px] flex-col gap-7 shadow-xs transition-[padding] duration-200 lg:my-auto',
              isFullScreenStep ? 'p-10' : 'p-6',
            )}
          >
            {isFullScreenStep ? (
              <WizardHeader />
            ) : (
              <>
                <WizardHeader
                  currentStep={state.currentStep}
                  isFirstStep={isFirstStep}
                  isSaving={isSaving}
                  onBack={handleBack}
                  totalSteps={totalSteps}
                />
                <WizardProgress currentStep={state.currentStep} totalSteps={totalSteps} />
              </>
            )}
            <StepTransition
              direction={showNextSteps ? 'forward' : direction}
              stepKey={showNextSteps ? 'next-steps' : `step-${state.currentStep}`}
            >
              {renderStep()}
            </StepTransition>
            {!isFullScreenStep && (
              <div className="mt-auto flex flex-col gap-2">
                {savingError && <p className="text-sm text-destructive">{savingError}</p>}
                <WizardNavigation
                  disabled={!isStepComplete}
                  isFirstStep={isFirstStep}
                  isLast={isLastStep}
                  isLoading={isSaving}
                  onBack={handleBack}
                  onNext={handleAdvance}
                />
              </div>
            )}
          </Card>
        </div>
      </WizardContainer>
    </FormProvider>
  );
};

/**
 * Onboarding wizard page — rendered as a fullscreen overlay anchored to the viewport.
 * Mounted via route (/onboarding); covers the app shell entirely.
 */
export const OnboardingWizardPage = () => {
  return (
    <div className="fixed inset-0 z-50 bg-background">
      <OnboardingWizardProvider>
        <WizardContent />
      </OnboardingWizardProvider>
    </div>
  );
};
